import React, { useState } from 'react';
import { Button } from '../Button';
import { BookOpenIcon, EditIcon, DeleteIcon, PlusIcon, SaveIcon } from './DashboardIcons';

// Simplified initial data for demonstration
const initialCountryData = [
  {
    name: 'España',
    flag: '🇪🇸',
    traffic: { speed: `120 km/h`, alcohol: `0.5 g/L`, lights: `En túneles`, safetyEquipment: `2 triángulos, chaleco`, notes: `ZBE en ciudades.` },
    connectivity: { mobile: `Buena`, internet: `Fibra extendida` },
    languages: { main: `Español`, secondary: `Inglés en turismo` }
  },
  {
    name: 'Francia',
    flag: '🇫🇷',
    traffic: { speed: `130 km/h`, alcohol: `0.5 g/L`, lights: `Recomendadas`, safetyEquipment: `Chaleco, triángulo`, notes: `Peajes, Crit'Air.` },
    connectivity: { mobile: `Excelente`, internet: `Fibra extendida` },
    languages: { main: `Francés`, secondary: `Inglés` }
  },
];

type Country = typeof initialCountryData[0];

const emptyCountry: Country = {
    name: '', flag: '', 
    traffic: { speed: '', alcohol: '', lights: '', safetyEquipment: '', notes: '' },
    connectivity: { mobile: '', internet: '' },
    languages: { main: '', secondary: '' }
};

export const GuidesView: React.FC = () => {
    const [countries, setCountries] = useState(initialCountryData);
    const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
    const [isCreating, setIsCreating] = useState(false);

    const handleSelectCountry = (country: Country) => {
        setSelectedCountry({ ...country });
        setIsCreating(false);
    };
    
    const handleAddNew = () => {
        setSelectedCountry({ ...emptyCountry });
        setIsCreating(true);
    };

    const handleCancel = () => {
        setSelectedCountry(null);
        setIsCreating(false);
    };

    const handleSave = () => {
        if (!selectedCountry) return;

        if (isCreating) {
            setCountries([...countries, selectedCountry].sort((a,b) => a.name.localeCompare(b.name)));
        } else {
            setCountries(countries.map(c => c.name === selectedCountry.name ? selectedCountry : c));
        }
        handleCancel();
    };

    const handleDelete = (countryName: string) => {
        if (window.confirm(`¿Seguro que quieres eliminar la guía de ${countryName}?`)) {
            setCountries(countries.filter(c => c.name !== countryName));
            if (selectedCountry?.name === countryName) {
                handleCancel();
            }
        }
    };

    const handleInputChange = (section: keyof Country, field: string, value: string) => {
        if (!selectedCountry) return;
        
        const updatedCountry = { ...selectedCountry };
        (updatedCountry[section] as any)[field] = value;
        setSelectedCountry(updatedCountry);
    };

    const FormInput: React.FC<{ label: string; value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void }> = ({ label, value, onChange }) => (
        <div>
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            <input type="text" value={value} onChange={onChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
        </div>
    );
    
    return (
        <div className="p-8 bg-white rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center"><BookOpenIcon /><span className="ml-2">Gestión de Guías de Países</span></h1>
                 <Button onClick={handleAddNew} variant="primary" className="bg-blue-600 hover:bg-blue-700 text-white">
                    <span className="flex items-center"><PlusIcon /> <span className="ml-2">Crear Guía</span></span>
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Country List */}
                <div className="md:col-span-1">
                    <h2 className="text-lg font-semibold text-gray-700 mb-3">Países Disponibles</h2>
                    <ul className="space-y-2">
                        {countries.map(country => (
                            <li key={country.name}
                                className={`flex justify-between items-center p-3 rounded-lg cursor-pointer transition-colors ${selectedCountry?.name === country.name ? 'bg-blue-100' : 'hover:bg-gray-100'}`}
                                onClick={() => handleSelectCountry(country)}
                            >
                                <span className="font-medium">{country.flag} {country.name}</span>
                                <div className="flex items-center space-x-2">
                                    <button onClick={(e) => { e.stopPropagation(); handleSelectCountry(country); }} className="text-yellow-500 hover:text-yellow-700"><EditIcon /></button>
                                    <button onClick={(e) => { e.stopPropagation(); handleDelete(country.name); }} className="text-red-500 hover:text-red-700"><DeleteIcon /></button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Edit Form */}
                <div className="md:col-span-2">
                    {selectedCountry ? (
                        <div className="bg-gray-50 p-6 rounded-lg border">
                            <h2 className="text-xl font-semibold mb-4">{isCreating ? 'Nueva Guía' : `Editando ${selectedCountry.name}`}</h2>
                            
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <FormInput label="Nombre del País" value={selectedCountry.name} onChange={e => setSelectedCountry({...selectedCountry, name: e.target.value})} />
                                <FormInput label="Emoji Bandera" value={selectedCountry.flag} onChange={e => setSelectedCountry({...selectedCountry, flag: e.target.value})} />
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <h3 className="font-semibold text-gray-800 border-b pb-1 mb-3">Normas de Tráfico</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <FormInput label="Velocidad" value={selectedCountry.traffic.speed} onChange={e => handleInputChange('traffic', 'speed', e.target.value)} />
                                        <FormInput label="Alcoholemia" value={selectedCountry.traffic.alcohol} onChange={e => handleInputChange('traffic', 'alcohol', e.target.value)} />
                                        <FormInput label="Luces" value={selectedCountry.traffic.lights} onChange={e => handleInputChange('traffic', 'lights', e.target.value)} />
                                        <FormInput label="Equipamiento" value={selectedCountry.traffic.safetyEquipment} onChange={e => handleInputChange('traffic', 'safetyEquipment', e.target.value)} />
                                        <div className="col-span-2">
                                            <label className="block text-sm font-medium text-gray-700">Notas</label>
                                            <textarea value={selectedCountry.traffic.notes} onChange={e => handleInputChange('traffic', 'notes', e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" rows={2}></textarea>
                                        </div>
                                    </div>
                                </div>
                                 <div>
                                    <h3 className="font-semibold text-gray-800 border-b pb-1 mb-3">Conectividad</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <FormInput label="Móvil" value={selectedCountry.connectivity.mobile} onChange={e => handleInputChange('connectivity', 'mobile', e.target.value)} />
                                        <FormInput label="Internet" value={selectedCountry.connectivity.internet} onChange={e => handleInputChange('connectivity', 'internet', e.target.value)} />
                                    </div>
                                </div>
                                 <div>
                                    <h3 className="font-semibold text-gray-800 border-b pb-1 mb-3">Idiomas</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <FormInput label="Principal" value={selectedCountry.languages.main} onChange={e => handleInputChange('languages', 'main', e.target.value)} />
                                        <FormInput label="Secundario" value={selectedCountry.languages.secondary} onChange={e => handleInputChange('languages', 'secondary', e.target.value)} />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex justify-end gap-3 mt-6">
                                <Button onClick={handleCancel} variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-200">Cancelar</Button>
                                <Button onClick={handleSave} variant="success">
                                    <span className="flex items-center"><SaveIcon /> <span className="ml-2">Guardar Guía</span></span>
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg border-2 border-dashed">
                            <p className="text-gray-500">Selecciona un país para editar o crea uno nuevo.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};